import { GoogleRatingStatus } from '@esg/shared';
import { ClientReviewCreateResponse, createClientReview } from '@esg/business-client-review';
import { AppButton, Block, FlexBox, FormBase, RateField, Spacer, TextCommon, TextAreaField, useHttpCommand } from '@esg/ui';
import { Card, Image } from 'antd';
import { useCallback } from 'react';
import { viewRequests } from '@esg/framework';
import '../Home.scss';
import { translateCodes } from '@/locales';
import { useTranslation } from 'react-i18next';

export interface ReviewFormValues {
  readonly id?: string;
  readonly rating?: number;
  readonly feedback?: string;
  readonly customerId?: string;
  readonly googleRatingStatus?: GoogleRatingStatus;
  readonly saleId?: string;
  readonly branchId?: string;
  readonly creationTime?: string;
}
interface ReviewFormProps {
  defaultValues?: ReviewFormValues;
  handleShowSuccess: (value: ClientReviewCreateResponse) => void;
  headerRequest?: Record<string, string>;
  customerId?: string;
  saleId?: string;
  branchId?: string;
  urlLogo?: string;
  handleSetActivePanel?: (key: "LEFT" | "RIGHT") => void;
  isMobile?: boolean;
  salonName?: string;
}

export function ReviewForm(props: ReviewFormProps) {
  const { t } = useTranslation();

  const { mutateAsync: createReview, isPending: isLoadingSubmit } = useHttpCommand(createClientReview);

  const onSubmit = useCallback(
    async (values: ReviewFormValues) => {
      await createReview(
        {
          pathData: {
            reviewToken: props.saleId
          },
          header: props.headerRequest,
          body: {
            rating: values.rating ?? 0,
            feedback: values.feedback,
            customerId: props.customerId,
            saleId: props.saleId,
            branchId: props.branchId,
            googleRatingStatus: values.rating === 5 ? GoogleRatingStatus.None : undefined
          }
        },
        {
          onSuccess: (data) => {
            props.handleShowSuccess(data);
          },
          onError: () => {
            viewRequests.showNotification.send({
              type: 'error',
              title: t(translateCodes.REVIEW_EXISTS),
              message: t(translateCodes.SALE_REVIEWED),
            });
          }
        }
      );
    },
    [createReview]
  );

  return (
    <Block width='100%'>
      <FormBase onSubmit={onSubmit}>
        <Block className='review-form'>
          <Card >
            <FlexBox direction={'column'} gap={30} alignItems='center'>
              <Spacer height={10} />
              <Image width={100} preview={false} src={props.urlLogo} ></Image>
              <FlexBox direction='column' alignItems='center'>
                <TextCommon fontWeight={700} fontSize={22} translateCode={translateCodes.RATE_EXPERIENCE} />
                <TextCommon fontWeight={700} fontSize={22}>{props.salonName}</TextCommon>
                <Spacer height={10} />
                {props.isMobile && (
                  <a
                    onClick={() => props.handleSetActivePanel?.('LEFT')}
                    style={{
                      color: '#1890ff',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    {t(translateCodes.VIEW_TRANSACTION_DETAILS)}
                  </a>
                )}
                <Spacer height={10} />
                <RateField
                  name="rating"
                  allowHalf={false}
                />
              </FlexBox>
              <Block width='100%'>
                <TextCommon color='#344054' translateCode={translateCodes.GIVE_FEEDBACK} />
                <TextAreaField
                  title='Give feedback'
                  name='feedback'
                  rows={7}
                  maxLength={600}
                  placeholder={t(translateCodes.ENTER_FEEDBACK)}
                  label={''}
                />
                <TextCommon color='#D0D5DD'>0/600</TextCommon>
              </Block>
              <AppButton type="primary" width='100%' htmlType='submit' loading={isLoadingSubmit}>
                {t(translateCodes.SUBMIT)}
              </AppButton>
            </FlexBox>
          </Card>
        </Block>
      </FormBase>
    </Block>
  );
}
